import { Link } from "react-router-dom";
import { ClimbingArea } from "../types";
import { areaLink } from "../utils/climbingAreaUtils";
import { forecastDayConditions, parseNumber, rateForecastDay } from "../utils/conditions";
import { dayLabel, dayNote } from "../utils/areaForecast";
import { RatingButton } from "./ratingButton";
import { WeatherIcon } from "./weatherIcon";
import "./areaRow.css";

interface AreaRowProps {
  area: ClimbingArea;
  /// Hide when rows are already grouped under the state's name
  showState?: boolean;
  /// How many days of forecast to show (narrow phones show only the first)
  days?: number;
  onClick?: () => void;
}

/// One area as a tappable row: name, today's summary and a short forecast.
/// The name is the link, stretched over the whole row; each rating is its own
/// button on top, because a button can't sit inside a link.
export function AreaRow({ area, showState = true, days = 2, onClick }: AreaRowProps) {
  const tz = area.forecast?.timezone ?? "UTC";
  const daily = (area.forecast?.daily?.data ?? []).slice(0, days);
  const now = Date.now() / 1000;
  const today = area.forecast && daily[0] ? rateForecastDay(area.forecast, 0) : null;
  const sub = [showState ? area.adminAreaName : null, today ? dayNote(today) : null].filter(Boolean).join(" · ");

  return (
    <div className="area-row">
      <span className="area-row__info">
        <Link to={areaLink(area)} className="area-row__link" onClick={onClick}>
          <span className="area-row__name">{area.name}</span>
        </Link>
        {sub && <span className="area-row__sub">{sub}</span>}
      </span>
      {daily.length > 0 && (
        <span className="area-row__days">
          {daily.map((dp, i) => {
            const high = parseNumber(dp.temperatureHigh);
            return (
              <span className="area-row__day" key={dp.time}>
                <span className="area-row__dow">{dayLabel(dp.time, tz, now, true)}</span>
                <span className="area-row__wx">
                  <WeatherIcon code={dp.icon} size={24} />
                  <span className="area-row__temp">
                    {high !== undefined ? `${Math.round(high)}°` : "–"}
                    <span className="cw-sr-only"> high</span>
                  </span>
                  <RatingButton
                    conditions={forecastDayConditions(area.forecast!, i)}
                    period="day"
                    context={`${area.name} · ${dayLabel(dp.time, tz, now)}`}
                  />
                </span>
              </span>
            );
          })}
        </span>
      )}
      <svg className="area-row__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </div>
  );
}

/// Placeholder row while forecasts load
export function AreaRowSkeleton() {
  return (
    <div className="area-row area-row--loading" aria-hidden="true">
      <span className="area-row__info">
        <span className="cw-skeleton" style={{ width: "60%", marginTop: 0 }} />
        <span className="cw-skeleton cw-skeleton--short" style={{ marginTop: 8 }} />
      </span>
    </div>
  );
}
